<template>
  <div class="model-management-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:cpu-chip" width="24" height="24" />
        创作模型管理
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="searchForm.writing_name" placeholder="请输入接口名称" clearable size="large" class="search-input"
            @keyup.enter="handleSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="handleSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>
        </div>

        <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
          <Icon icon="heroicons:plus" width="18" height="18" />
          <span class="btn-text">新增模型</span>
        </el-button>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table :data="modelList" stripe v-loading="loading" class="model-table"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">
          <el-table-column prop="writing_name" label="接口名称" width="180" />
          <el-table-column prop="writing_type_text" label="创作类型" width="120">
            <template #default="{ row }">
              <span class="type-badge" :class="`type-${row.writing_type}`">
                {{ row.writing_type_text }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="key_preview" label="密钥预览" width="180">
            <template #default="{ row }">
              <span class="key-preview">{{ row.key_preview }}******</span>
            </template>
          </el-table-column>
          <el-table-column prop="writing_url" label="接口地址" min-width="200" show-overflow-tooltip />
          <el-table-column prop="writing_model" label="模型名称" width="150" />
          <el-table-column prop="notes" label="备注" width="150" />
          <el-table-column prop="create_time" label="创建时间" width="180" />
          <el-table-column prop="update_time" label="更新时间" width="180" />

          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <button class="action-btn edit-btn" @click="openEditDialog(scope.row)">
                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                <span class="btn-text">编辑</span>
              </button>
              <button class="action-btn delete-btn" @click="handleDelete(scope.row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout"
          @size-change="fetchModelList" @current-change="fetchModelList" background />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" :width="dialogWidth" :close-on-click-modal="false"
      class="custom-dialog" :top="dialogTop">
      <el-form ref="modelForm" :model="modelForm" :label-width="formLabelWidth" :rules="formRules">
        <el-form-item label="接口名称" prop="writing_name">
          <el-input v-model="modelForm.writing_name" placeholder="请输入接口名称" size="large" />
        </el-form-item>

        <el-form-item label="写作类型" prop="writing_type">
          <el-select v-model="modelForm.writing_type" placeholder="请选择写作类型" size="large">
            <el-option v-for="type in writingTypes" :key="type.value" :label="type.label" :value="type.value" />
          </el-select>
        </el-form-item>

        <el-form-item label="预设模型">
          <el-select v-model="selectedPresetModel" placeholder="请选择预设模型" size="large" @change="handlePresetModelChange"
            clearable>
            <el-option v-for="model in presetModels" :key="model.value" :label="model.label" :value="model" />
          </el-select>
        </el-form-item>

        <el-form-item label="接口地址" prop="writing_url">
          <el-input v-model="modelForm.writing_url" placeholder="请输入写作接口地址" size="large" />
        </el-form-item>

        <el-form-item label="接口密钥" prop="writing_key">
          <el-input v-model="modelForm.writing_key" placeholder="请输入接口密钥" size="large" show-password />
        </el-form-item>

        <el-form-item label="模型名称" prop="writing_model">
          <el-input v-model="modelForm.writing_model" placeholder="请输入模型名称" size="large" />
        </el-form-item>

        <el-form-item label="测试连通性">
          <el-button type="primary" size="large" @click="testConnection" :loading="testingConnection"
            :disabled="!modelForm.writing_url || !modelForm.writing_key || !modelForm.writing_model">
            <Icon icon="heroicons:signal" width="18" height="18" />
            测试连通性
          </el-button>
          <span class="test-result" :class="{ 'success': testResult === 'success', 'error': testResult === 'error' }">
            {{ testResult === 'success' ? '✓ 测试通过' : testResult === 'error' ? '✗ 测试失败' : '' }}
          </span>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="modelForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitModelForm">确认</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'SystemModel',
  components: { Icon },
  data() {
    const validateUrl = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入接口地址'));
      } else if (!/^https?:\/\/.+/.test(value)) {
        callback(new Error('请输入有效的URL地址'));
      } else {
        callback();
      }
    };
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增模型',
      isEditMode: false,
      testingConnection: false,
      testResult: '',
      searchForm: { writing_name: '' },
      presetModels: [
        { label: '智谱大模型', value: 'glm-4-flash', url: 'https://open.bigmodel.cn/api/paas/v4/chat/completions', modelName: 'glm-4-flash' },
        { label: '阿里通义千问', value: 'qwen-max', url: 'https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions', modelName: 'qwen-max' },
        { label: '百度千帆大模型', value: 'ernie-4.0-8k', url: 'https://qianfan.baidubce.com/v2/chat/completions', modelName: 'ernie-4.0-8k' },
        { label: '腾讯混元大模型', value: 'hunyuan-turbo-latest', url: 'https://api.hunyuan.cloud.tencent.com/v1/chat/completions', modelName: 'hunyuan-turbo-latest' },
        { label: 'DeepSeek深度求索', value: 'deepseek-chat', url: 'https://api.deepseek.com/chat/completions', modelName: 'deepseek-chat' },
        { label: '火山方舟豆包/DeepSeek满血版', value: 'glm-4-flash-volc', url: 'https://ark.cn-beijing.volces.com/api/v3/chat/completions', modelName: 'glm-4-flash' },
        { label: 'Kimi月之暗面', value: 'moonshot-v1-128k', url: 'https://api.moonshot.cn/v1/chat/completions', modelName: 'moonshot-v1-128k' },
        { label: '自定义大模型', value: 'custom', url: '', modelName: '' }
      ],
      selectedPresetModel: null,
      writingTypes: [
        { label: '描述创作', value: '1' },
        { label: '标题创作', value: '2' },
        { label: '话题创作', value: '3' },
        { label: '文案创作', value: '4' }
      ],
      modelForm: { id: null, writing_name: '', writing_type: '1', writing_url: '', writing_key: '', writing_model: '', notes: '' },
      formRules: {
        writing_name: [{ required: true, message: '请输入接口名称', trigger: 'blur' }],
        writing_type: [{ required: true, message: '请选择写作类型', trigger: 'change' }],
        writing_url: [{ required: true, validator: validateUrl, trigger: 'blur' }],
        writing_key: [{ required: true, message: '请输入接口密钥', trigger: 'blur' }],
        writing_model: [{ required: true, message: '请输入模型名称', trigger: 'blur' }]
      },
      modelList: [],
      pagination: { page: 1, pageSize: 10, total: 0 },
      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '600px'
    },
    dialogTop() {
      if (this.screenWidth < 576) return '5vh'
      return '10vh'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '100px'
    }
  },
  mounted() {
    this.fetchModelList();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() { this.screenWidth = window.innerWidth },
    async testConnection() {
      if (!this.modelForm.writing_url || !this.modelForm.writing_key || !this.modelForm.writing_model) {
        ElMessage.warning('请先填写接口地址、密钥和模型名称');
        return;
      }
      this.testingConnection = true;
      this.testResult = '';
      try {
        const response = await axios.post(
          this.modelForm.writing_url,
          { model: this.modelForm.writing_model, messages: [{ "role": "system", "content": "You are a helpful assistant." }, { "role": "user", "content": "Hello!" }], stream: false },
          { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.modelForm.writing_key}` }, timeout: 10000 }
        );
        if (response.data && response.data.choices) { this.testResult = 'success'; ElMessage.success('模型连接测试成功！'); }
        else { this.testResult = 'error'; ElMessage.error('接口返回格式不符合预期'); }
      } catch (error) {
        this.testResult = 'error';
        let errorMessage = '连接测试失败';
        if (error.response) { errorMessage += ` (状态码: ${error.response.status})`; }
        else if (error.request) { errorMessage += ': 服务器无响应'; }
        else { errorMessage += `: ${error.message}`; }
        ElMessage.error(errorMessage);
      } finally { this.testingConnection = false; }
    },
    handlePresetModelChange(model) {
      if (model && model.value !== 'custom') { this.modelForm.writing_url = model.url; this.modelForm.writing_model = model.modelName; }
      else if (model && model.value === 'custom') { this.modelForm.writing_url = ''; this.modelForm.writing_model = ''; }
    },
    async fetchModelList() {
      this.loading = true;
      try {
        const params = { action: this.searchForm.writing_name ? 'search' : 'list', store_id: this.userInfo.id, page: this.pagination.page, page_size: this.pagination.pageSize };
        if (this.searchForm.writing_name) params.writing_name = this.searchForm.writing_name;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/WritingModelKeyController.php`, params);
        if (data.data) { this.modelList = data.data; this.pagination.total = data.pagination?.total || data.data.length; }
      } catch (error) { ElMessage.error('获取模型列表失败'); }
      finally { this.loading = false; }
    },
    handleSearch() { this.pagination.page = 1; this.fetchModelList(); },
    openAddDialog() {
      this.dialogTitle = '新增模型'; this.isEditMode = false; this.testResult = '';
      this.modelForm = { id: null, writing_name: '', writing_type: '1', writing_url: '', writing_key: '', writing_model: '', notes: '' };
      this.selectedPresetModel = null;
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      this.dialogTitle = '编辑模型'; this.isEditMode = true; this.testResult = '';
      this.modelForm = { id: row.id, writing_name: row.writing_name, writing_type: row.writing_type, writing_url: row.writing_url, writing_key: row.writing_key, writing_model: row.writing_model, notes: row.notes };
      const matchedModel = this.presetModels.find(model => model.url === row.writing_url && model.modelName === row.writing_model);
      this.selectedPresetModel = matchedModel || null;
      this.dialogVisible = true;
    },
    submitModelForm() {
      this.$refs.modelForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = { ...this.modelForm, store_id: this.userInfo.id };
            const { data } = await axios.post(`${config.BASE_URL}/shop/api/WritingModelKeyController.php`, { action, ...payload });
            if (data.success) { ElMessage.success(this.isEditMode ? '更新成功' : '添加成功'); this.dialogVisible = false; this.fetchModelList(); }
            else { ElMessage.error(data.error || '操作失败'); }
          } catch (error) { ElMessage.error('操作失败'); }
        }
      });
    },
    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除该模型配置吗？', '警告', { confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning' });
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/WritingModelKeyController.php`, { action: 'delete', id: id, store_id: this.userInfo.id });
        if (data.success) { ElMessage.success('删除成功'); this.fetchModelList(); }
        else { ElMessage.error(data.error || '删除失败'); }
      } catch (error) { if (error !== 'cancel') ElMessage.error('删除失败'); }
    }
  }
}
</script>

<style scoped lang="scss">
.model-management-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .search-left .search-btn {
      width: 100%;
    }

    .add-btn {
      width: 100%;
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

:deep(.el-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;
  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  .el-table-fixed-column--right {
    background-color: #120e21 !important;
    backdrop-filter: none !important;
  }

  th.el-table-fixed-column--right {
    background-color: #2a1f45 !important;
  }

  .el-table__row--striped .el-table-fixed-column--right {
    background-color: #1a1430 !important;
  }

  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }
}

.key-preview {
  font-family: monospace;
  background: rgba(157, 78, 221, 0.15);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  color: #c084fc;
}

.type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  &.type-1 {
    background: rgba(59, 130, 246, 0.2);
    color: #60a5fa;
  }

  &.type-2 {
    background: rgba(34, 197, 94, 0.2);
    color: #4ade80;
  }

  &.type-3 {
    background: rgba(245, 158, 11, 0.2);
    color: #fbbf24;
  }

  &.type-4 {
    background: rgba(234, 98, 14, 0.2);
    color: #fb923c;
  }
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin: 0 4px;

  &.edit-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
    }
  }

  &.delete-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
    }
  }

  @media (max-width: 576px) {
    .btn-text {
      display: none;
    }
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }
}

:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }
}

:deep(.el-form-item__label) {
  color: #a0a0c0;
  font-weight: 500;
}

:deep(.el-input__wrapper) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  box-shadow: none;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &.is-focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  .el-input__inner {
    color: #fff;
    background: transparent;

    &::placeholder {
      color: #5a5a7a;
    }
  }
}

:deep(.el-textarea__inner) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  color: #fff;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &:focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }
  }
}

:deep(.el-button) {
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

:deep(.el-button--primary) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

:deep(.el-button--success) {
  background: linear-gradient(135deg, #10b981, #34d399);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

.test-result {
  margin-left: 15px;
  font-size: 14px;

  &.success {
    color: #4ade80;
  }

  &.error {
    color: #f87171;
  }
}

@media (max-width: 480px) {
  .btn-text {
    display: none;
  }
}
</style>

<!-- 非 scoped 全局样式 -->
<style lang="scss">
.el-dialog {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.custom-dialog) .el-dialog__body {
  background: rgba(18, 14, 33, 0.98) !important;
}
</style>